import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar, ListChecks, Sparkle } from "@phosphor-icons/react";
import BulkAttendance from "@/components/BulkAttendance";

const HARI_LIST = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Ahad"];
const DIVISI_TO_STATUS = {
  keibadahan: ["hadir", "izin", "alfa", "sakit", "berhalangan"],
  lugho: ["aktif_berbahasa", "tidak_aktif_berbahasa", "izin", "alfa"],
  kedisiplinan: ["hadir", "izin", "alfa", "sakit"],
  kebersihan: ["hadir", "izin", "alfa", "sakit"],
  keamanan: ["hadir", "izin", "alfa", "sakit"],
  kesehatan: ["sehat", "sakit", "izin_berobat", "alfa"],
  humas: ["hadir", "izin", "alfa", "sakit"],
  logistik: ["hadir", "izin", "alfa", "sakit"],
};

// Auto-detect today's day (Indonesian)
function todayHari() {
  const idx = new Date().getDay(); // 0=Ahad ... 6=Sabtu
  return ["Ahad", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"][idx];
}

export default function Kegiatan() {
  const [hari, setHari] = useState(todayHari());
  const [tanggal, setTanggal] = useState(() => new Date().toISOString().slice(0, 10));
  const [jadwal, setJadwal] = useState([]);
  const [selectedJadwal, setSelectedJadwal] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadJadwal = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/master/jadwal_kegiatan`, { params: { hari } });
      setJadwal(data);
      setSelectedJadwal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadJadwal(); /* eslint-disable-next-line */ }, [hari]);

  const status = selectedJadwal ? (DIVISI_TO_STATUS[selectedJadwal.divisi] || DIVISI_TO_STATUS.kedisiplinan) : null;
  const izinKey = selectedJadwal?.divisi === "kesehatan" ? "izin_berobat" : "izin";

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Kegiatan Pondok</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">Absensi Kegiatan</h1>
        <p className="text-sm text-slate-500 mt-1">
          Pilih <b>hari</b> dan <b>tanggal</b>. Sistem menampilkan kegiatan yang telah dijadwalkan Administrator via Pengaturan → Jadwal Kegiatan.
        </p>
      </div>

      <div className="imsyaf-card p-5 grid md:grid-cols-3 gap-3">
        <div><label className="text-xs">Hari *</label>
          <select className="imsyaf-input" value={hari} onChange={(e) => setHari(e.target.value)} data-testid="keg-hari">
            {HARI_LIST.map((h) => <option key={h} value={h}>{h}</option>)}
          </select></div>
        <div><label className="text-xs">Tanggal *</label>
          <input type="date" className="imsyaf-input" value={tanggal} onChange={(e) => setTanggal(e.target.value)} data-testid="keg-tanggal" /></div>
        <div className="flex items-end">
          <button className="imsyaf-btn-outline w-full" onClick={loadJadwal} data-testid="keg-refresh">
            <Calendar size={14}/> Muat Jadwal
          </button>
        </div>
      </div>

      <div className="imsyaf-card p-5">
        <div className="text-xs uppercase tracking-widest text-[#166534] mb-3">
          Jadwal Kegiatan — {hari}
        </div>
        {loading ? (
          <div className="text-sm text-slate-400 py-4">Memuat jadwal…</div>
        ) : jadwal.length === 0 ? (
          <div className="text-sm text-slate-400 py-4">
            Tidak ada kegiatan terjadwal untuk hari <b>{hari}</b>. Administrator perlu menambahkan di Pengaturan → Jadwal Kegiatan.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {jadwal.map((j) => (
              <button
                key={j.id}
                onClick={() => setSelectedJadwal(j)}
                data-testid={`jadwal-${j.id}`}
                className={`text-left p-4 rounded-lg border-2 transition ${
                  selectedJadwal?.id === j.id
                    ? "border-[#14532d] bg-green-50"
                    : "border-slate-200 hover:border-[#166534]/60 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-start gap-2">
                  <Sparkle size={16} className="text-[#166534] mt-0.5" weight="fill" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#064E3B]">{j.nama}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {j.jam_mulai || "-"}{j.jam_selesai ? ` – ${j.jam_selesai}` : ""}
                      {j.divisi ? ` · ${j.divisi}` : ""}
                    </div>
                    {j.lokasi && <div className="text-[11px] text-slate-400">{j.lokasi}</div>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedJadwal && (
        <div className="space-y-3">
          <div className="imsyaf-card p-4 bg-green-50 border-green-200">
            <div className="text-xs text-green-800/70 uppercase tracking-widest">Mengabsen</div>
            <div className="font-display text-lg font-bold text-[#064E3B]">
              <ListChecks size={18} className="inline -mt-1 mr-1"/> {selectedJadwal.nama}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {hari}, {tanggal} · Divisi {selectedJadwal.divisi || "kedisiplinan"}
            </div>
          </div>
          <BulkAttendance
            endpoint="/absensi-divisi"
            basePayload={{
              divisi: selectedJadwal.divisi || "kedisiplinan",
              kegiatan: selectedJadwal.nama,
              tanggal,
              hari,
              jam: selectedJadwal.jam_mulai,
            }}
            statuses={status}
            izinKey={izinKey}
            femaleOnlyStatuses={["berhalangan"]}
            onDone={() => toast.success("Absensi kegiatan tersimpan")}
          />
        </div>
      )}
    </div>
  );
}
